/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { XIcon } from 'lucide-react';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui/sheet';
import Search from './search';
import SearchButton from './search-button';

interface LocationKeyedSearchProps {
    onSubmitSearch: () => void;
    trailingAction: ReactElement;
}

function LocationKeyedSearch({ onSubmitSearch, trailingAction }: LocationKeyedSearchProps): ReactElement {
    const location = useLocation();
    return (
        <Search
            key={`${location.pathname}${location.search}`}
            onSubmitSearch={onSubmitSearch}
            trailingAction={trailingAction}
            suggestionsPanelDefaultOpen
        />
    );
}

export default function SearchSheet(): ReactElement {
    const { t } = useTranslation('header');
    const [open, setOpen] = useState(false);

    const handleOpenChange = useCallback((isOpen: boolean) => {
        setOpen(isOpen);
    }, []);

    const handleSubmitSearch = useCallback(() => {
        setOpen(false);
    }, []);

    return (
        <>
            <SearchButton onClick={() => setOpen(true)} />
            <Sheet open={open} onOpenChange={handleOpenChange}>
                <SheetContent
                    showCloseButton={false}
                    className="w-full max-w-full lg:w-[760px] lg:max-w-[760px] overflow-y-auto">
                    <SheetTitle className="sr-only">{t('searchPlaceholder')}</SheetTitle>
                    <LocationKeyedSearch
                        onSubmitSearch={handleSubmitSearch}
                        trailingAction={
                            <SheetClose
                                className="ring-offset-background focus:ring-ring mt-4 shrink-0 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
                                <XIcon className="size-6" />
                                <span className="sr-only">Close</span>
                            </SheetClose>
                        }
                    />
                </SheetContent>
            </Sheet>
        </>
    );
}
